import type { ComponentProps, ComponentType, ReactElement, ReactNode } from 'react';
import { Suspense, lazy, useState } from 'react';
import { useInRouterContext, useParams } from 'react-router';

import { logger } from '@luna/logger';

import { createAsyncDependenciesContext } from './createAsyncDependenciesContext';
import { getAsyncDependenciesCache, requestWithSuspense } from './requestWithSuspense';
import { LazyComponentErrorBoundary } from './LazyComponentErrorBoundary';

interface AsyncDependenciesOptions<Data, UrlParams> {
    asyncDependencies: (arg: { urlParams: UrlParams }) => Promise<Data>;
    errorFallback: ReactNode;
    /**
     * @example withParamsType: {} as { accountId: string } */
    withUrlParamsType?: UrlParams;
}

// extends AsyncDependenciesOptions and make all its props optional via Partial
interface BaseOptions<Data, UrlParams> extends Partial<AsyncDependenciesOptions<Data, UrlParams>> {
    fallback?: ReactNode;
}

// if Options contains `asyncDependencies` make AsyncDependenciesOptions required props not optional
type Options<Data, UrlParams, T extends BaseOptions<Data, UrlParams>> =
    T extends Pick<AsyncDependenciesOptions<Data, UrlParams>, 'asyncDependencies'>
        ? AsyncDependenciesOptions<Data, UrlParams>
        : T;

type LazyComponent<Component extends ComponentType<any>, Data> = ((
    props: ComponentProps<Component>,
) => React.ReactNode) & {
    preload: () => Promise<Component>;
    useAsyncDependencies: () => Data | undefined;
};

const ERROR_MESSAGE =
    'Component was not resolved in the async import. This can be caused by invalid import path or by error in the lazy loaded module.';
/**
 * Factory function to create a Lazy version of a React component.
 * @return
 * A React Component which loads the original React component only when it is rendered.
 * The created component shall be prefixed by `Lazy*` or suffixed with `*Loadable`.
 * @example
 * ```
 * const LazyComponent = createLazyComponent(() => import('./Component'));
 * ```
 * */
export const createLazyComponent = <
    // let these Generics be inferred, do not explicitly specify them
    Component extends ComponentType<any>,
    Data,
    UrlParams extends Record<string, string>,
    T extends BaseOptions<Data, UrlParams>,
>(
    importFunc: () => Promise<Component>,
    options?: Options<Data, UrlParams, T> & T,
): LazyComponent<Component, Data> => {
    const lazyFactory: () => Promise<{ default: Component }> = () =>
        importFunc().then((component: Component) => {
            if (!component) {
                logger.error(ERROR_MESSAGE, component);
                throw Error(ERROR_MESSAGE);
            }

            return { default: component };
        });

    const LazyComponent = lazy(lazyFactory);

    const AsyncDependenciesContext = createAsyncDependenciesContext<Data>();

    const LazyComponentWithSuspense: LazyComponent<Component, Data> = (props) => {
        const [asyncDependenciesData, setAsyncDependenciesData] = useState<Data | undefined>();

        const cleanupFailedCachedAsyncDependencies = () => {
            if (!options?.asyncDependencies) {
                return;
            }

            getAsyncDependenciesCache().delete(options.asyncDependencies);
        };

        const asyncDependencies = options?.asyncDependencies;

        return (
            <LazyComponentErrorBoundary
                errorFallback={options?.errorFallback}
                onErrorCleanup={cleanupFailedCachedAsyncDependencies}
            >
                <Suspense fallback={options?.fallback ?? null}>
                    {!!asyncDependencies && (
                        <AsyncDependenciesContext.Provider value={asyncDependenciesData}>
                            <WithAsyncDependencies<Data, UrlParams>
                                asyncDependencies={asyncDependencies}
                                importFunc={importFunc}
                                onAsyncDependenciesResolved={(data) => {
                                    if (asyncDependenciesData === data) {
                                        return;
                                    }
                                    setAsyncDependenciesData(data);
                                }}
                            >
                                <LazyComponent {...props} />
                            </WithAsyncDependencies>
                        </AsyncDependenciesContext.Provider>
                    )}
                    {!asyncDependencies && <LazyComponent {...props} />}
                </Suspense>
            </LazyComponentErrorBoundary>
        );
    };

    LazyComponentWithSuspense.preload = importFunc;
    LazyComponentWithSuspense.useAsyncDependencies = () => {
        // Keep useContext for proper React reactivity and re-render subscriptions
        const contextValue = AsyncDependenciesContext.consumer();
        if (typeof contextValue !== 'undefined') {
            return contextValue;
        }

        // During concurrent rendering, context may not be committed yet — read from cache directly
        const asyncDeps = options?.asyncDependencies;
        if (!asyncDeps) {
            return undefined;
        }
        const cached = getAsyncDependenciesCache().get(asyncDeps);
        return cached?.resolved ? (cached.data as Data) : undefined;
    };

    return LazyComponentWithSuspense;
};

type WithAsyncDependenceProps<Data, UrlParams> = {
    children: ReactNode;
    asyncDependencies: (arg: { urlParams: UrlParams }) => Promise<Data>;
    importFunc: () => Promise<ComponentType<any>>;
    onAsyncDependenciesResolved: (data: Data) => void;
};

function WithAsyncDependencies<Data, UrlParams extends Record<string, string>>({
    children,
    asyncDependencies,
    importFunc,
    onAsyncDependenciesResolved,
}: WithAsyncDependenceProps<Data, UrlParams>): ReactElement {
    if (__DEV__) {
        if (!useInRouterContext()) {
            throw new Error(
                'createLazyComponent asyncDependencies option can only be used within a @luna/router router',
            );
        }
    }
    const urlParams = useParams<UrlParams>();
    requestWithSuspense<Data, UrlParams>(
        asyncDependencies,
        importFunc,
        { urlParams },
        onAsyncDependenciesResolved,
    );

    return children as ReactElement;
}
